import { REGION_OPTIONS, TAG_OPTIONS, YEAR_OPTIONS } from '@/types/media';

interface FilterBarProps {
  year: string;
  region: string;
  tag: string;
  sort: string;
  mobileOpen: boolean;
  onToggleMobile: () => void;
  onYearChange: (value: string) => void;
  onRegionChange: (value: string) => void;
  onTagChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onReset: () => void;
}

interface ChipRowProps {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
}

function ChipRow({ label, options, value, onChange }: ChipRowProps) {
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 py-3 border-b border-background-200 last:border-b-0">
      <span className="shrink-0 sm:w-16 text-sm font-medium text-foreground-600 pt-1">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className={`px-3 h-8 rounded-full text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
              value === option
                ? 'bg-primary-600 text-background-50'
                : 'bg-background-100 border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-700'
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterBar({
  year,
  region,
  tag,
  sort,
  mobileOpen,
  onToggleMobile,
  onYearChange,
  onRegionChange,
  onTagChange,
  onSortChange,
  onReset,
}: FilterBarProps) {
  const hasFilter = year !== '全部' || region !== '全部' || tag !== '全部';

  return (
    <div className="rounded-lg bg-background-50 border border-background-200/80">
      <div className="flex items-center justify-between px-4 md:px-5 py-3 md:py-0">
        <button
          type="button"
          onClick={onToggleMobile}
          className="md:hidden flex items-center gap-2 text-sm font-medium text-foreground-800 cursor-pointer"
        >
          <i className="ri-filter-3-line text-accent-600"></i>
          筛选条件
          <i className={`ri-arrow-down-s-line transition-transform ${mobileOpen ? 'rotate-180' : ''}`}></i>
        </button>
        <span className="hidden md:flex items-center gap-2 text-sm font-medium text-foreground-800">
          <i className="ri-filter-3-line text-accent-600"></i>
          筛选条件
        </span>
        <div className="flex items-center gap-3">
          {hasFilter && (
            <button
              type="button"
              onClick={onReset}
              className="text-xs md:text-sm text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              清空筛选
            </button>
          )}
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-xs md:text-sm text-foreground-500">排序</span>
            <select
              value={sort}
              onChange={(event) => onSortChange(event.target.value)}
              className="h-8 px-2 rounded-md border border-background-300 bg-background-100 text-xs md:text-sm text-foreground-800 focus:outline-none focus:border-primary-400 cursor-pointer"
            >
              <option value="latest">最新</option>
              <option value="hot">热门</option>
            </select>
          </div>
        </div>
      </div>

      <div className={`px-4 md:px-5 ${mobileOpen ? 'block' : 'hidden'} md:block`}>
        <ChipRow label="年份" options={YEAR_OPTIONS} value={year} onChange={onYearChange} />
        <ChipRow label="地区" options={REGION_OPTIONS} value={region} onChange={onRegionChange} />
        <ChipRow label="题材" options={TAG_OPTIONS} value={tag} onChange={onTagChange} />
      </div>
    </div>
  );
}